// DNA sequence-related functions
import * as con from "../Constants"

/**
 * FASTA validation workflow
 *
 * 1. Clean input: normalize CRLF to '\n', trim whitespace
 * 2. Empty input → error
 * 3. No '>' → single sequence provided: strip whitespace, uppercase, validate, add header '>Query'
 * 4. Else → split on lines starting with '>', enforce maxRecords limit:
 *    For each record: extract header, uppercase/concat seq, validate chars
 * 5. If sequence total length > maxTotalLength? → error
 * 6. Success → [con.STATUS_OK, con.SEQUENCE_OK, cleaned]; failure → [con.STATUS_ERROR, message, raw]
 */




// only A C G T U and all IUPAC ambiguity codes:
const VALID_NUC_REGEX = /^[ACGTURYKMSWBDHVN]+$/i;
const FASTA_HEADER_RX = /^>(.*)/;

/**
 * Normalize line endings, trim trailing whitespace
 */
function sanitizeRawInput(text) {
  return text
    .replace(/\r\n/g, '\n')
    .trim();
}

/**
 * If there's no '>' anywhere, treat as single raw sequence
 */
function isSingleSequence(text) {
  return !text.includes('>');
}

/**
 * Split on each '>' at start of line, but keep the '>' with each record
 */
function splitFastaRecords(text) {
  // ensure leading '>' so split works
  const withLead = text.startsWith('>') ? text : '>' + text;
  // split and drop empty first element
  return withLead
    .split(/(?=^>)/gm)
    .filter(Boolean);
}

/**
 * Parse one FASTA record into { header, sequence }
 * Throws if header line missing.
 */
function parseFastaRecord(record) {
  const lines = record.split('\n');
  const match = FASTA_HEADER_RX.exec(lines[0]);
  if (!match) {
    throw new Error(con.SEQUENCE_MESSAGE_ERR_INVALID_FORMAT);
  }
  const header = match[1].trim() || 'Unnamed';
  const seq = lines
    .slice(1)
    .map(l => l.trim().toUpperCase())
    .join('');
  return { header, seq };
}

/**
 * Check that seq only has valid nucleotide letters
 */
function validateSequenceChars(seq) {
  return VALID_NUC_REGEX.test(seq);
}

/**
 * Main entry point for processing and cleaning input sequences
 *
 * @param {string} textInput  raw user‐pasted text
 * @param {object} [options]
 * @param {number} [options.maxRecords=10]
 * @param {number} [options.maxTotalLength=100000]
 * @returns {[string, object, string]}
 *   [ status, { messsage, context}, cleanedText ]
 */
export function processAndCleanInputSequences(
  textInput,
  {
    maxRecords = con.DEFAULT_MAX_RECORDS,
    maxTotalLength = con.DEFAULT_MAX_TOTAL_LENGTH,
  } = {}
) {
  const raw = sanitizeRawInput(textInput);

  // 1) empty?
  if (!raw) {
    return [con.STATUS_ERROR, { [con.MESSAGE]: con.SEQUENCE_MESSAGE_ERR_NO_SEQUENCE }, raw];
  }

  // 2) single‐line sequence without any '>' → wrap it
  if (isSingleSequence(raw)) {
    const seq = raw.replace(/\s+/g, '').toUpperCase();
    if (!seq) {
      return [con.STATUS_ERROR, { [con.MESSAGE]: con.SEQUENCE_MESSAGE_ERR_NO_SEQUENCE }, raw];
    }
    if (!validateSequenceChars(seq)) {
      return [con.STATUS_ERROR, { [con.MESSAGE]: con.SEQUENCE_MESSAGE_ERR_INVALID_CHARACTERS }, raw];
    }
    const cleaned = `>Query\n${seq}`;
    return [con.STATUS_OK, { [con.MESSAGE]: con.SEQUENCE_MESSAGE_OK }, cleaned];
  }

  // 3) multi‐FASTA: split into records
  let records;
  try {
    records = splitFastaRecords(raw);
  } catch (e) {
    return [con.STATUS_ERROR, { [con.MESSAGE]: con.SEQUENCE_MESSAGE_ERR_INVALID_FORMAT }, raw];
  }

  // 4) enforce record count
  if (records.length > maxRecords) {
    return [con.STATUS_ERROR, { [con.MESSAGE]: con.SEQUENCE_MESSAGE_ERR_TOO_MANY_RECORDS, [con.CONTEXT]: { maxRecords } }, raw];
  }

  // 5) parse, validate, accumulate
  let totalLen = 0;
  const cleanedRecs = [];
  try {
    for (const rec of records) {
      const { header, seq } = parseFastaRecord(rec);
      if (!validateSequenceChars(seq)) {
        throw new Error(con.SEQUENCE_MESSAGE_ERR_INVALID_CHARACTERS);
      }
      totalLen += seq.length;
      cleanedRecs.push(`>${header}\n${seq}`);
    }
  } catch (e) {
    return [con.STATUS_ERROR, { [con.MESSAGE]: e.message }, raw];
  }

  // 6) enforce total length
  if (totalLen > maxTotalLength) {
    return [con.STATUS_ERROR, { [con.MESSAGE]: con.SEQUENCE_MESSAGE_ERR_TOTAL_LENGTH, [con.CONTEXT]: { maxTotalLength } }, raw];
  }

  // 7) all good → return joined, cleaned FASTA
  const output = cleanedRecs.join('\n');
  return [con.STATUS_OK, { [con.MESSAGE]: con.SEQUENCE_MESSAGE_OK }, output];
}

